<script>
  import Icon from "@iconify/svelte";
  import serverIcon from "@iconify/icons-mdi/cog";
  import tagIcon from "@iconify/icons-mdi/tag-outline";
  import { t } from "./lib/i18n.js";

  export let apiBase = "";
  export let apiKey = "";
  export let setup = null;
  export let setupErr = "";
  export let browseHosts = [];
  export let locale = "en";
  export let onApiError = (_msg) => {};

  function item(label, value, hint = "") {
    return { label, value, hint };
  }

  function section(title, items) {
    return { title, items };
  }

  function pretty(v) {
    const s = (v ?? "").toString().trim();
    return s ? s : "—";
  }

  function maskSecret(v) {
    const s = (v ?? "").toString().trim();
    if (!s) return "—";
    if (s.length <= 2) return "****";
    return s[0] + "****" + s[s.length - 1];
  }

  function isSecret(name) {
    const n = (name ?? "").toUpperCase();
    return n.includes("PASSWORD") || n.includes("SECRET") || n.includes("_KEY");
  }

  function groupEnvVars(envVars = []) {
    const grouped = new Map();
    for (const env of envVars) {
      const key = (env?.name || "General").toString();
      let group;
      if (key === "DEFAULT_WEBHOOKS" || key.startsWith("WEBHOOK_")) {
        group = "Webhooks";
      } else {
        group = key.includes("_") ? key.split("_")[0] : "General";
      }
      if (!grouped.has(group)) grouped.set(group, []);
      grouped.get(group).push(env);
    }
    return Array.from(grouped.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([group, items]) => ({ group, items }));
  }

  function orderedSections(envVars) {
    const groups = groupEnvVars(envVars);
    const storeConnection = section(t(locale, "settings_store_connection"), [
      item(t(locale, "settings_engine"), pretty(setup.store_engine)),
      item(t(locale, "settings_version"), pretty(setup.redis_info?.version)),
      item(t(locale, "settings_mode"), pretty(setup.redis_info?.mode)),
      item(t(locale, "settings_memory"), pretty(setup.redis_info?.used_memory_human)),
      item(t(locale, "settings_connected_clients"), pretty(setup.redis_info?.connected_clients)),
      item(t(locale, "settings_ops_sec"), pretty(setup.redis_info?.ops_per_sec)),
      item(t(locale, "settings_lzf_possible"), setup.store_lzf_possible ? t(locale, "settings_yes") : t(locale, "settings_no")),
      item(t(locale, "settings_auth"), setup.auth_enabled ? t(locale, "settings_enabled") : t(locale, "settings_disabled"))
    ]);
    const envSections = groups.map((g) =>
      section(
        g.group,
        g.items.map((env) => {
          const raw = pretty(env.value || env.default);
          const display = isSecret(env.name) ? maskSecret(env.value || env.default) : raw;
          return item(
            env.name,
            display,
            env.value ? "" : env.default ? t(locale, "settings_default") : ""
          );
        })
      )
    );
    const storeGroup = envSections.find((s) => s.title === "STORE");
    const webhooksGroup = envSections.find((s) => s.title === "Webhooks");
    const rest = envSections.filter(
      (s) => s.title !== "STORE" && s.title !== "Webhooks"
    );
    return [
      storeConnection,
      ...(storeGroup ? [section(t(locale, "settings_store"), storeGroup.items)] : []),
      ...(webhooksGroup ? [webhooksGroup] : []),
      ...rest
    ];
  }

  $: sections = setup ? orderedSections(setup.env_vars || []) : [];

  const mono =
    "font-mono text-xs text-zinc-200 whitespace-nowrap overflow-hidden text-ellipsis";
  const sectionCard = "rounded-lg border border-white/10 bg-black/20 p-4 sm:p-5";
  const cell = "rounded-lg border border-white/10 bg-white/[0.03] p-3 min-w-0";
  const klabel = "text-xs text-zinc-400 flex flex-wrap items-center gap-2";
  const khint =
    "text-[11px] text-zinc-500 inline-flex items-center gap-1 whitespace-nowrap";
</script>

<main class="w-full mt-6">
  <section class="ui-card p-5 sm:p-6">
    <!-- Header -->
    <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div class="flex items-start gap-3">
        <span
          class="inline-flex h-9 w-9 items-center justify-center border border-slate-300/60 bg-slate-600/50 text-slate-50"
        >
          <Icon icon={serverIcon} width="18" height="18" />
        </span>

        <div>
          <h1 class="text-lg font-semibold tracking-wide text-zinc-100">
            {t(locale, "settings_title")}
          </h1>
          <p class="mt-0.5 text-sm text-zinc-400">
            {t(locale, "settings_subtitle")}
          </p>
        </div>
      </div>
    </div>

    <!-- Error / Loading -->
    {#if setupErr}
      <div
        class="mt-4 rounded-lg border border-rose-400/35 bg-rose-400/10 px-4 py-3 text-sm text-rose-100 flex items-center gap-2"
      >
        <span class="break-words">{setupErr}</span>
      </div>
    {/if}

    {#if !setup}
      <div
        class="mt-6 rounded-lg border border-white/10 bg-black/20 p-4 text-sm text-zinc-400"
      >
        {t(locale, "loading")}
      </div>
    {:else}
      <!-- Store hosts (when multiple or single) -->
      {#if browseHosts.length > 0}
        <div class={sectionCard + " mt-6"}>
          <div class="flex items-baseline justify-between gap-3 border-b border-white/10 pb-3">
            <div class="flex items-center gap-2">
              <Icon icon={tagIcon} width="16" height="16" />
              <div class="text-sm font-semibold tracking-wide text-white">{t(locale, "settings_store_hosts")}</div>
            </div>
            <div class="text-xs text-zinc-500">{t(locale, "settings_host_count", { count: browseHosts.length })}</div>
          </div>
          <div class="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {#each browseHosts as h (h.id)}
              <div class={cell}>
                <div class={klabel}>{t(locale, "settings_host")}</div>
                <div class={`${mono} mt-2`} title={h.addr}>
                  {h.label || h.addr}
                </div>
                <div class="text-[11px] text-zinc-500 mt-1 font-mono">{h.addr}</div>
              </div>
            {/each}
          </div>
        </div>
      {/if}

      <!-- Sections -->
      <div class="mt-6 grid grid-cols-1 gap-4">
        {#each sections as s (s.title)}
          <div class={sectionCard}>
            <div
              class="flex items-baseline justify-between gap-3 border-b border-white/10 pb-3"
            >
              <div class="flex items-center gap-2">
                <Icon icon={tagIcon} width="16" height="16" />
                <div class="text-sm font-semibold tracking-wide text-white">
                  {s.title}
                </div>
              </div>

              <div class="text-xs text-zinc-500">{t(locale, "settings_items", { count: s.items.length })}</div>
            </div>

            <div
              class="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
            >
              {#each s.items as it (it.label)}
                <div class={cell}>
                  <div class={klabel}>
                    <span>{it.label}</span>
                    {#if it.hint}
                      <span class={khint}>• {it.hint}</span>
                    {/if}
                  </div>

                  <div class={`${mono} mt-2`} title={it.value}>
                    {it.value}
                  </div>
                </div>
              {/each}
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </section>
</main>
